import React from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/joy';
import { Trash as Delete, Warning } from '@phosphor-icons/react';
import { useDeleteContactNote } from '../../../hooks/useCrm';
import type { ContactNote } from '../../../types/crm';

interface DeleteNoteModalProps {
  open: boolean;
  onClose: () => void;
  note: ContactNote | null;
}

export const DeleteNoteModal: React.FC<DeleteNoteModalProps> = ({
  open,
  onClose,
  note,
}) => {
  const deleteNoteMutation = useDeleteContactNote();

  const handleDelete = async () => {
    if (!note) return;

    try {
      await deleteNoteMutation.mutateAsync({
        id: note.id,
        contactId: note.contact,
      });
      onClose();
    } catch {
      // Intentionally empty
    }
  };

  if (!note) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: '90vw', maxWidth: 400 }}>
        <ModalClose />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Warning color="var(--joy-palette-warning-500)" />
            <Typography level="h4">
              Delete Note
            </Typography>
          </Stack>

          <Typography level="body-md">
            Are you sure you want to delete this note? This action cannot be undone.
          </Typography>

          {note.subject && (
            <Alert variant="soft" color="neutral">
              <Typography level="body-sm" fontWeight="md">
                "{note.subject}"
              </Typography>
            </Alert>
          )}

          {deleteNoteMutation.isError && (
            <Alert color="danger">
              Failed to delete note. Please try again.
            </Alert>
          )}

          <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={deleteNoteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              startDecorator={deleteNoteMutation.isPending ? <CircularProgress size="sm" /> : <Delete />}
              onClick={handleDelete}
              disabled={deleteNoteMutation.isPending}
            >
              {deleteNoteMutation.isPending ? 'Deleting...' : 'Delete Note'}
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};